import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  BarChart3,
  Megaphone,
  ListChecks,
  Users,
  Workflow,
  Plus,
  Trash2,
  Copy,
  Sparkles,
  ExternalLink,
  Check,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import { useAuth } from "@/contexts/AuthContext";

const CHART_COLORS = ["#F97316", "#A855F7", "#14B8A6", "#EAB308", "#F43F5E", "#3B82F6"];
const TABS = [
  { key: "overview", label: "Overview", icon: BarChart3 },
  { key: "forms", label: "Forms", icon: Megaphone },
  { key: "funnels", label: "Funnels", icon: Workflow },
  { key: "tasks", label: "Tasks", icon: ListChecks },
  { key: "leads", label: "Leads", icon: Users },
];

export default function MarketingCRM() {
  const [tab, setTab] = useState("overview");
  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="marketing-crm">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-primary mb-2">Marketing CRM</div>
          <h1 className="font-display text-4xl leading-tight">Capture. Nurture. Convert.</h1>
          <p className="text-muted-foreground mt-1">Forms, drip funnels, tasks and campaign analytics — all in one place.</p>
        </div>
      </div>

      <div className="inline-flex flex-wrap rounded-full border border-border bg-secondary/50 p-1">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`mkt-tab-${t.key}`}
              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.2em] transition-colors ${
                tab === t.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon className="h-3.5 w-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "overview" && <OverviewView />}
      {tab === "forms" && <FormsView />}
      {tab === "funnels" && <FunnelsView />}
      {tab === "tasks" && <TasksView />}
      {tab === "leads" && <LeadsView />}
    </div>
  );
}

// ================================================================
// Overview / Analytics
// ================================================================
function OverviewView() {
  const [a, setA] = useState(null);
  useEffect(() => {
    api.get("/marketing/analytics").then(({ data }) => setA(data)).catch(() => toast.error("Could not load analytics"));
  }, []);
  if (!a) return <div className="py-16 text-center text-muted-foreground">Loading analytics…</div>;

  const sourceData = Object.entries(a.leads_by_source || {}).map(([name, value]) => ({ name, value }));

  return (
    <div className="space-y-6" data-testid="mkt-overview">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Leads" value={a.totals.leads} testid="mkt-kpi-leads" />
        <Kpi label="Forms" value={a.totals.forms} />
        <Kpi label="Funnels" value={a.totals.funnels} />
        <Kpi label="Pending tasks" value={a.totals.tasks_pending} testid="mkt-kpi-tasks-pending" />
        <Kpi label="Conversion" value={`${a.totals.conversion_pct}%`} />
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2 rounded-xl border border-border bg-card p-4">
          <h2 className="font-display text-lg mb-3">Leads · last 14 days</h2>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <LineChart data={a.leads_last_14d}>
                <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#a1a1aa" }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 11, fill: "#a1a1aa" }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#111", border: "1px solid #333" }} />
                <Line type="monotone" dataKey="leads" stroke="#F97316" strokeWidth={2} dot={{ r: 3, fill: "#F97316" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-4">
          <h2 className="font-display text-lg mb-3">Leads by source</h2>
          {sourceData.length ? (
            <div style={{ width: "100%", height: 220 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={sourceData} dataKey="value" nameKey="name" outerRadius={72} label={(e) => `${e.name} (${e.value})`}>
                    {sourceData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#111", border: "1px solid #333" }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-muted-foreground text-sm py-8 text-center">No leads yet.</div>
          )}
        </div>
      </div>

      {a.funnel_stats.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-4">
          <h2 className="font-display text-lg mb-3">Funnel completion</h2>
          <div className="space-y-4">
            {a.funnel_stats.map((fn) => (
              <div key={fn.id}>
                <div className="text-sm font-semibold mb-2">{fn.name}</div>
                <div className="grid gap-2">
                  {fn.steps.map((s) => (
                    <div key={s.index} className="flex items-center gap-3">
                      <div className="w-40 truncate text-xs text-muted-foreground">Day {s.day_offset} · {s.subject}</div>
                      <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden">
                        <div className="h-full bg-primary rounded-full" style={{ width: `${s.completion_pct}%` }} />
                      </div>
                      <div className="w-24 text-right text-xs">{s.done}/{s.done + s.pending + s.skipped} · {s.completion_pct}%</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ================================================================
// Forms
// ================================================================
function FormsView() {
  const [forms, setForms] = useState([]);
  const [funnels, setFunnels] = useState([]);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const { tenant } = useAuth();

  const load = async () => {
    try {
      const [{ data: fs }, { data: fn }] = await Promise.all([
        api.get("/marketing/forms"),
        api.get("/marketing/funnels"),
      ]);
      setForms(fs); setFunnels(fn);
    } catch { toast.error("Could not load forms"); }
  };
  useEffect(() => { load(); }, []);

  const save = async (payload) => {
    try {
      if (payload.id) {
        const { id, ...rest } = payload;
        const { data } = await api.patch(`/marketing/forms/${id}`, rest);
        setForms((prev) => prev.map((f) => (f.id === id ? data : f)));
        toast.success("Form saved");
      } else {
        const { data } = await api.post("/marketing/forms", payload);
        setForms((prev) => [data, ...prev]);
        toast.success("Form created");
      }
      setEditing(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/marketing/forms/${id}`);
      setForms((prev) => prev.filter((f) => f.id !== id));
      toast.success("Form deleted");
    } catch { toast.error("Delete failed"); }
    setConfirmDel(null);
  };

  const publicUrl = (slug) => `${window.location.origin}/f/${tenant?.slug}/${slug}`;

  return (
    <div className="space-y-4" data-testid="mkt-forms">
      <div className="flex justify-end">
        <Button onClick={() => setEditing({ __new: true, name: "", fields: [{ key: "name", label: "Full name", type: "text", required: true }, { key: "email", label: "Email", type: "email", required: true }], funnel_id: "", intro_text: "", success_message: "Thanks — we'll be in touch shortly." })} className="rounded-full gap-1.5" data-testid="mkt-new-form-btn">
          <Plus className="h-4 w-4" /> New form
        </Button>
      </div>
      {forms.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground border border-dashed border-border rounded-xl">
          No forms yet. Create one and share the public link.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {forms.map((f) => {
            const url = publicUrl(f.slug);
            const funnel = funnels.find((fn) => fn.id === (typeof f.funnel_id === 'string' ? f.funnel_id : ''));
            return (
              <div key={f.id} className="rounded-xl border border-border bg-card p-4 space-y-2" data-testid={`mkt-form-row-${f.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-semibold">{f.name}</div>
                    <div className="text-xs text-muted-foreground">{f.fields?.length || 0} fields · {f.submissions || 0} submissions</div>
                    {funnel && <div className="text-xs text-primary mt-0.5">→ {funnel.name}</div>}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => setEditing({ ...f })} className="text-xs px-2 py-1 rounded-md hover:bg-muted" data-testid={`mkt-form-edit-${f.id}`}>Edit</button>
                    <button onClick={() => setConfirmDel(f)} className="p-1.5 rounded-md hover:bg-muted text-destructive" data-testid={`mkt-form-delete-${f.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs bg-secondary/50 rounded-md p-2">
                  <ExternalLink className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <a href={url} target="_blank" rel="noreferrer" className="truncate flex-1 hover:text-primary" data-testid={`mkt-form-url-${f.id}`}>{url}</a>
                  <button onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(url);
                      toast.success("Copied");
                    } catch {
                      // Fallback for permission-denied contexts
                      try {
                        const ta = document.createElement("textarea");
                        ta.value = url; document.body.appendChild(ta); ta.select();
                        document.execCommand("copy"); document.body.removeChild(ta);
                        toast.success("Copied");
                      } catch {
                        toast.error("Copy failed — select the URL and copy manually.");
                      }
                    }
                  }} className="p-1 rounded hover:bg-muted" data-testid={`mkt-form-copy-${f.id}`}><Copy className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {editing && <FormEditor form={editing} funnels={funnels} onSave={save} onClose={() => setEditing(null)} />}

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this form?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDel && `"${confirmDel.name}" will be removed. The public URL will stop working immediately. This cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => del(confirmDel.id)} data-testid="mkt-form-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function FormEditor({ form, funnels, onSave, onClose }) {
  const [state, setState] = useState({
    ...form,
    fields: form.fields || [],
    funnel_id: form.funnel_id || "",
    intro_text: form.intro_text || "",
    success_message: form.success_message || "",
  });
  const setField = (i, patch) => setState((s) => ({ ...s, fields: s.fields.map((f, idx) => idx === i ? { ...f, ...patch } : f) }));
  const addField = () => setState((s) => ({ ...s, fields: [...s.fields, { key: `field_${s.fields.length + 1}`, label: "New field", type: "text", required: false }] }));
  const rmField = (i) => setState((s) => ({ ...s, fields: s.fields.filter((_, idx) => idx !== i) }));

  const submit = (e) => {
    e.preventDefault();
    if (!state.name?.trim()) return toast.error("Form name is required");
    if (state.fields.length === 0) return toast.error("Add at least one field");
    const { __new, id, slug, submissions, tenant_id, created_at, ...rest } = state;
    onSave({ ...(id ? { id } : {}), ...rest, funnel_id: rest.funnel_id || null });
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="mkt-form-editor">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{form.__new ? "New form" : "Edit form"}</DialogTitle>
          <DialogDescription>Fields become the questions on the public capture page.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs">Form name *</Label>
            <Input value={state.name} onChange={(e) => setState((s) => ({ ...s, name: e.target.value }))} data-testid="mkt-form-name" required />
          </div>
          <div>
            <Label className="text-xs">Attach funnel (auto-schedules tasks on submit)</Label>
            <Select value={state.funnel_id || "none"} onValueChange={(v) => setState((s) => ({ ...s, funnel_id: v === "none" ? "" : v }))}>
              <SelectTrigger data-testid="mkt-form-funnel"><SelectValue placeholder="No funnel" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No funnel</SelectItem>
                {funnels.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Intro text (optional)</Label>
            <Textarea rows={2} value={state.intro_text} onChange={(e) => setState((s) => ({ ...s, intro_text: e.target.value }))} placeholder="Shown at the top of your public form" />
          </div>
          <div>
            <Label className="text-xs">Success message</Label>
            <Input value={state.success_message} onChange={(e) => setState((s) => ({ ...s, success_message: e.target.value }))} />
          </div>

          <div className="space-y-2 rounded-lg border border-border p-3">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Fields</div>
              <Button type="button" size="sm" variant="outline" className="rounded-full h-7 gap-1" onClick={addField} data-testid="mkt-form-add-field"><Plus className="h-3.5 w-3.5" />Add</Button>
            </div>
            {state.fields.map((f, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <Input className="col-span-3" placeholder="key" value={f.key} onChange={(e) => setField(i, { key: e.target.value })} data-testid={`mkt-form-field-key-${i}`} />
                <Input className="col-span-4" placeholder="Label" value={f.label} onChange={(e) => setField(i, { label: e.target.value })} data-testid={`mkt-form-field-label-${i}`} />
                <Select value={f.type} onValueChange={(v) => setField(i, { type: v })}>
                  <SelectTrigger className="col-span-2" data-testid={`mkt-form-field-type-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["text", "email", "phone", "textarea", "select"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
                <label className="col-span-2 flex items-center gap-1.5 text-xs">
                  <input type="checkbox" checked={f.required} onChange={(e) => setField(i, { required: e.target.checked })} />
                  required
                </label>
                <button type="button" onClick={() => rmField(i)} className="col-span-1 p-1.5 rounded hover:bg-muted text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
            <Button type="submit" className="rounded-full" data-testid="mkt-form-save">Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ================================================================
// Funnels
// ================================================================
function FunnelsView() {
  const [funnels, setFunnels] = useState([]);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/marketing/funnels"); setFunnels(data); }
    catch { toast.error("Could not load funnels"); }
  };
  useEffect(() => { load(); }, []);

  const save = async (payload) => {
    try {
      if (payload.id) {
        const { id, ...rest } = payload;
        const { data } = await api.patch(`/marketing/funnels/${id}`, rest);
        setFunnels((p) => p.map((f) => f.id === id ? data : f));
        toast.success("Funnel saved");
      } else {
        const { data } = await api.post("/marketing/funnels", payload);
        setFunnels((p) => [data, ...p]);
        toast.success("Funnel created");
      }
      setEditing(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const del = async (id) => {
    try {
      await api.delete(`/marketing/funnels/${id}`);
      setFunnels((p) => p.filter((f) => f.id !== id));
      toast.success("Funnel deleted");
    } catch { toast.error("Delete failed"); }
    setConfirmDel(null);
  };

  return (
    <div className="space-y-4" data-testid="mkt-funnels">
      <div className="flex justify-end">
        <Button onClick={() => setEditing({ __new: true, name: "", description: "", steps: [] })} className="rounded-full gap-1.5" data-testid="mkt-new-funnel-btn">
          <Plus className="h-4 w-4" /> New funnel
        </Button>
      </div>
      {funnels.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground border border-dashed border-border rounded-xl">
          No funnels yet. Draft a welcome sequence with AI in seconds.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {funnels.map((fn) => (
            <div key={fn.id} className="rounded-xl border border-border bg-card p-4" data-testid={`mkt-funnel-row-${fn.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-semibold">{fn.name}</div>
                  {fn.description && <div className="text-xs text-muted-foreground">{fn.description}</div>}
                  <div className="text-xs text-muted-foreground mt-1">{fn.steps?.length || 0} steps</div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setEditing({ ...fn })} className="text-xs px-2 py-1 rounded-md hover:bg-muted" data-testid={`mkt-funnel-edit-${fn.id}`}>Edit</button>
                  <button onClick={() => setConfirmDel(fn)} className="p-1.5 rounded-md hover:bg-muted text-destructive" data-testid={`mkt-funnel-delete-${fn.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
              <div className="mt-3 space-y-1">
                {(fn.steps || []).map((s, i) => (
                  <div key={i} className="text-xs bg-secondary/50 rounded-md px-2 py-1.5 flex gap-2">
                    <span className="text-primary shrink-0">Day {s.day_offset}</span>
                    <span className="truncate">{s.subject}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && <FunnelEditor funnel={editing} onSave={save} onClose={() => setEditing(null)} />}

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this funnel?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDel && `"${confirmDel.name}" and its ${confirmDel.steps?.length || 0} steps will be removed. Existing leads keep their already-scheduled tasks. This cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => del(confirmDel.id)} data-testid="mkt-funnel-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function FunnelEditor({ funnel, onSave, onClose }) {
  const [state, setState] = useState({ ...funnel, steps: funnel.steps || [] });
  const [drafting, setDrafting] = useState(-1);

  const setStep = (i, patch) => setState((s) => ({ ...s, steps: s.steps.map((st, idx) => idx === i ? { ...st, ...patch } : st) }));
  const addStep = () => setState((s) => ({ ...s, steps: [...s.steps, { day_offset: (s.steps[s.steps.length - 1]?.day_offset ?? -1) + 3, channel: "task", subject: "", body: "" }] }));
  const rmStep = (i) => setState((s) => ({ ...s, steps: s.steps.filter((_, idx) => idx !== i) }));

  const draft = async (i) => {
    setDrafting(i);
    try {
      const { data } = await api.post("/marketing/funnels/draft-step", {
        goal: `Move a lead further down the ${state.name || 'welcome'} sequence — this is step ${i + 1}`,
      });
      setStep(i, { subject: data.subject || state.steps[i].subject, body: data.body || state.steps[i].body });
      toast.success("Drafted with Kairosa AI");
    } catch { toast.error("AI draft failed"); }
    setDrafting(-1);
  };

  const submit = (e) => {
    e.preventDefault();
    if (!state.name?.trim()) return toast.error("Name is required");
    const { __new, id, tenant_id, created_at, ...rest } = state;
    onSave({ ...(id ? { id } : {}), ...rest });
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="mkt-funnel-editor">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{funnel.__new ? "New funnel" : "Edit funnel"}</DialogTitle>
          <DialogDescription>A sequence of touch-points that fire when a lead submits any form linked to this funnel.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Name *</Label>
              <Input value={state.name} onChange={(e) => setState((s) => ({ ...s, name: e.target.value }))} data-testid="mkt-funnel-name" required />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Input value={state.description || ""} onChange={(e) => setState((s) => ({ ...s, description: e.target.value }))} />
            </div>
          </div>

          <div className="space-y-2 rounded-lg border border-border p-3">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Steps</div>
              <Button type="button" size="sm" variant="outline" className="rounded-full h-7 gap-1" onClick={addStep} data-testid="mkt-funnel-add-step"><Plus className="h-3.5 w-3.5" />Add step</Button>
            </div>
            {state.steps.length === 0 && <div className="text-xs text-muted-foreground py-4 text-center">No steps yet — add one to begin.</div>}
            {state.steps.map((s, i) => (
              <div key={i} className="rounded-md border border-border/60 p-3 space-y-2">
                <div className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-2">
                    <Label className="text-[10px]">Day offset</Label>
                    <Input type="number" value={s.day_offset} onChange={(e) => setStep(i, { day_offset: Number(e.target.value) || 0 })} />
                  </div>
                  <div className="col-span-3">
                    <Label className="text-[10px]">Channel</Label>
                    <Select value={s.channel} onValueChange={(v) => setStep(i, { channel: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["task", "email_draft", "sms_draft", "call"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-6">
                    <Label className="text-[10px]">Subject</Label>
                    <Input value={s.subject} onChange={(e) => setStep(i, { subject: e.target.value })} data-testid={`mkt-funnel-step-subject-${i}`} />
                  </div>
                  <button type="button" onClick={() => rmStep(i)} className="col-span-1 mt-4 p-1.5 rounded hover:bg-muted text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
                <div>
                  <Label className="text-[10px]">Body</Label>
                  <Textarea rows={2} value={s.body} onChange={(e) => setStep(i, { body: e.target.value })} data-testid={`mkt-funnel-step-body-${i}`} />
                </div>
                <div className="flex justify-end">
                  <Button type="button" size="sm" variant="outline" onClick={() => draft(i)} disabled={drafting === i} className="rounded-full h-7 gap-1" data-testid={`mkt-funnel-ai-draft-${i}`}>
                    <Sparkles className="h-3.5 w-3.5" /> {drafting === i ? "Drafting…" : "Draft with AI"}
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
            <Button type="submit" className="rounded-full" data-testid="mkt-funnel-save">Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ================================================================
// Tasks
// ================================================================
function TasksView() {
  const [tasks, setTasks] = useState([]);
  const [filter, setFilter] = useState("pending");

  const load = async () => {
    try {
      const { data } = await api.get("/marketing/tasks", { params: filter === "all" ? {} : { status: filter } });
      setTasks(data);
    } catch { toast.error("Could not load tasks"); }
  };
  useEffect(() => { load(); }, [filter]);

  const patch = async (id, status) => {
    try {
      await api.patch(`/marketing/tasks/${id}`, { status });
      setTasks((p) => p.map((t) => t.id === id ? { ...t, status } : t));
      toast.success(status === "done" ? "Marked done" : "Skipped");
    } catch { toast.error("Update failed"); }
  };

  return (
    <div className="space-y-3" data-testid="mkt-tasks">
      <div className="inline-flex rounded-full border border-border bg-secondary/50 p-1">
        {["pending", "done", "skipped", "all"].map((k) => (
          <button key={k} onClick={() => setFilter(k)} className={`rounded-full px-3 py-1 text-xs uppercase tracking-[0.18em] ${filter === k ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} data-testid={`mkt-tasks-filter-${k}`}>{k}</button>
        ))}
      </div>
      {tasks.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground border border-dashed border-border rounded-xl">No {filter} tasks.</div>
      ) : (
        <div className="grid gap-2">
          {tasks.map((t) => {
            const overdue = t.status === "pending" && new Date(t.due_at) < new Date();
            return (
              <div key={t.id} className={`rounded-xl border p-3 flex items-start gap-3 ${overdue ? "border-destructive/50 bg-destructive/5" : "border-border bg-card"}`} data-testid={`mkt-task-row-${t.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{t.channel}</span>
                    <span className={`text-[10px] uppercase tracking-widest ${overdue ? "text-destructive" : "text-muted-foreground"}`}>due {new Date(t.due_at).toLocaleDateString()}</span>
                    <span className="text-xs">→ {t.lead?.name || "—"}</span>
                  </div>
                  <div className="font-semibold mt-0.5">{t.subject}</div>
                  <div className="text-xs text-muted-foreground whitespace-pre-wrap">{t.body}</div>
                </div>
                {t.status === "pending" ? (
                  <div className="flex gap-1">
                    <button onClick={() => patch(t.id, "done")} className="p-2 rounded-md border border-border hover:bg-primary hover:text-primary-foreground" title="Mark done" data-testid={`mkt-task-done-${t.id}`}><Check className="h-3.5 w-3.5" /></button>
                    <button onClick={() => patch(t.id, "skipped")} className="p-2 rounded-md border border-border hover:bg-muted" title="Skip" data-testid={`mkt-task-skip-${t.id}`}><X className="h-3.5 w-3.5" /></button>
                  </div>
                ) : (
                  <span className={`text-xs uppercase tracking-widest ${t.status === "done" ? "text-primary" : "text-muted-foreground"}`}>{t.status}</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ================================================================
// Leads
// ================================================================
function LeadsView() {
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [confirm, setConfirm] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/marketing/leads");
      setLeads(data);
    } catch { toast.error("Could not load leads"); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => leads.filter((l) => {
    if (status !== "all" && l.status !== status) return false;
    if (q) {
      const s = q.toLowerCase();
      const hay = `${l.contact?.name || ""} ${l.contact?.email || ""} ${l.source || ""}`.toLowerCase();
      if (!hay.includes(s)) return false;
    }
    return true;
  }), [leads, q, status]);

  const setLeadStatus = async (id, s) => {
    try {
      const { data } = await api.patch(`/marketing/leads/${id}`, { status: s });
      setLeads((prev) => prev.map((l) => l.id === id ? data : l));
      toast.success("Updated");
    } catch { toast.error("Update failed"); }
  };

  const del = async () => {
    if (!confirm) return;
    try {
      await api.delete(`/marketing/leads/${confirm}`);
      setLeads((prev) => prev.filter((l) => l.id !== confirm));
      toast.success("Lead deleted");
    } catch { toast.error("Delete failed"); }
    setConfirm(null);
  };

  return (
    <div className="space-y-3" data-testid="mkt-leads">
      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Search name, email, source…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm" data-testid="mkt-leads-search" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40" data-testid="mkt-leads-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {["new", "contacted", "booked", "won", "lost"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto text-xs text-muted-foreground">{filtered.length} leads</div>
      </div>
      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-secondary/60 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            <tr>{["Name", "Email", "Source", "Status", "Captured", ""].map((h) => <th key={h} className="text-left px-3 py-2">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.map((l) => (
              <tr key={l.id} data-testid={`mkt-lead-row-${l.id}`}>
                <td className="px-3 py-2 font-semibold">{l.contact?.name || "—"}</td>
                <td className="px-3 py-2 text-muted-foreground">{l.contact?.email || "—"}</td>
                <td className="px-3 py-2">{l.source || "—"}</td>
                <td className="px-3 py-2">
                  <Select value={l.status || "new"} onValueChange={(v) => setLeadStatus(l.id, v)}>
                    <SelectTrigger className="h-7 w-32 text-xs" data-testid={`mkt-lead-status-${l.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["new", "contacted", "booked", "won", "lost"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{new Date(l.created_at).toLocaleDateString()}</td>
                <td className="px-3 py-2 text-right">
                  <button className="text-muted-foreground hover:text-destructive p-1" onClick={() => setConfirm(l.id)} data-testid={`mkt-lead-delete-${l.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">No leads match the filter.</td></tr>}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this lead?</AlertDialogTitle>
            <AlertDialogDescription>This also removes the associated funnel tasks.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={del} data-testid="mkt-lead-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Kpi({ label, value, testid }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
      <div className="font-display text-2xl mt-1">{value}</div>
    </div>
  );
}
